import React from 'react';
import Table, { ColumnConfig } from 'components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import CURRENCY from 'utils/currency';
import { cn } from 'lib/utils';
import { getPercentChange } from 'utils/dashboard';
import { AmountChangePercentLabel } from 'utils/status-formatter';
import usePayment from 'state/payment/hooks/usePayment';
import { formatDate } from 'utils/date-format';
import NoSummaryFound from 'components/summary-tab/no-summary-found';

interface IProps {
  prevFromDate: string;
  prevToDate: string;
}

interface ITxnSummaryTotals {
  text: string;
  totalAmount: number;
  totalTransactions: number;
  aov: number;
}

const TotalTransactionSummary = ({ prevFromDate, prevToDate }: IProps) => {
  const { payment } = usePayment();
  const { transactionSummary: summary } = payment;
  const tooltipText = `Compared to previous period: ${formatDate(
    prevFromDate,
    'MMM dd yyyy, hh:mm a'
  )} - ${formatDate(prevToDate, 'MMM dd yyyy, hh:mm a')}`;

  const totalAmountPercentChange = getPercentChange(
    summary.total_amount,
    summary.prev_total_amount
  );

  const totalTransactionsPercentChange = getPercentChange(
    summary.total_count,
    summary.prev_total_count
  );

  const aovPercentChange = getPercentChange(
    summary.total_amount / summary.total_count,
    summary.prev_total_amount / summary.prev_total_count
  );

  const products: ITxnSummaryTotals[] = [
    {
      text: 'TOTALS',
      totalAmount: summary.total_amount,
      totalTransactions: summary.total_count,
      aov: summary.total_amount / summary.total_count,
    },
  ];

  const columns: ColumnConfig<ITxnSummaryTotals>[] = [
    {
      key: 'text',
      label: '',
      renderCell: (row) => (
        <div className="text-gray-blue-90 text-base font-semibold leading-6">
          {row.text?.toUpperCase() || ''}
        </div>
      ),
    },
    {
      key: 'totalAmount',
      label: 'GROSS RECEIPTS',
      renderCell: (row) => (
        <div
          className={cn(
            'text-gray-blue-60 font-medium body-medium text-left flex',
            'text-gray-blue-90 text-base font-semibold leading-6'
          )}
        >
          {row.totalAmount
            ? CURRENCY.convertToMainUnit(row.totalAmount)
            : '$0.00'}
          <span className="ml-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    {(totalAmountPercentChange ||
                      totalAmountPercentChange === 0) && (
                      <AmountChangePercentLabel value={totalAmountPercentChange} />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">{tooltipText}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
      ),
    },
    {
      key: 'totalTransactions',
      label: 'TOTAL TRANSACTIONS',
      renderCell: (row) => (
        <div
          className={cn(
            'text-gray-blue-60 font-medium body-medium text-left flex',
            'text-gray-blue-90 text-base font-semibold leading-6'
          )}
        >
          {row.totalTransactions}
          <span className="ml-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    {(totalTransactionsPercentChange ||
                      totalTransactionsPercentChange === 0) && (
                      <AmountChangePercentLabel
                        value={totalTransactionsPercentChange}
                      />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">{tooltipText}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
      ),
    },
    {
      key: 'aov',
      label: 'AOV',
      renderCell: (row) => (
        <div
          className={cn(
            'text-gray-blue-60 font-medium body-medium text-left flex',
            'text-gray-blue-90 text-base font-semibold leading-6'
          )}
        >
          {row.aov ? CURRENCY.convertToMainUnit(row.aov) : '$0.00'}
          <span className="ml-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    {(aovPercentChange || aovPercentChange === 0) && (
                      <AmountChangePercentLabel value={aovPercentChange} />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">{tooltipText}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
      ),
    },
  ];

  return (
    <Table
      id="totalTransactionSummary"
      data={products}
      columns={columns}
      theme={{
        showTableBorder: true,
        tableGridLayout: '1.6fr 1fr 1fr 1fr',
      }}
      customTheme={{
        HeaderCell: `height: 48px; background-color: #fff !important;`,
        Cell: `height: 64px; background-color: #f7f8f8;`,
        Table: `border-radius: 4px;`,
        Row: `
            & > td:first-child {
              border-bottom-left-radius: 4px;
            }
            & > td:last-child {
              border-bottom-right-radius: 4px;
            }
          `,
      }}
      noDataIndication={<NoSummaryFound />}
    />
  );
};

export default TotalTransactionSummary;
